import React from 'react';
import { style } from 'typestyle';
import * as styles from './display-screen.styles';

const RETRY_DELAY_MS = 30000;

interface Props {
  readonly children: React.ReactNode;
}

interface State {
  readonly hasError: boolean;
}

/**
 * Keeps a render-time exception from permanently killing an unattended screen.
 *
 * React unmounts the whole subtree when a component throws during render, and the kiosk has no
 * user to press reload: a lobby TV or a shared public link would sit blank until someone walked
 * over to it. This boundary catches the throw, shows a short holding message, and re-mounts the
 * screen after {@link RETRY_DELAY_MS} - the same cadence as the data poll - so a bad payload that
 * the next poll fixes costs half a minute rather than the rest of the day.
 *
 * It is a net, not a substitute for handling the payload properly at the point of use.
 */
class KioskErrorBoundary extends React.Component<Props, State> {
  private retryTimer: ReturnType<typeof setTimeout> | undefined;

  public constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error): void {
    // Sentry picks this up via the global handler; the log line is what someone debugging a screen
    // on site can read from a browser console.
    // eslint-disable-next-line no-console
    console.error('Kiosk display screen crashed; retrying shortly.', error);

    this.retryTimer = setTimeout(() => {
      this.setState({ hasError: false });
    }, RETRY_DELAY_MS);
  }

  public componentWillUnmount(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }

  public render(): React.ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className={style(styles.screen)}>
          <div className={style(styles.content)}>
            <div className={style(styles.pairingLabel)}>···</div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default KioskErrorBoundary;
